import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  MessageSquare,
  Send,
  Search,
  ArrowLeft,
  Crown,
  Shield,
  User as UserIcon,
  Circle,
  MoreVertical,
  Trash2,
  Ban,
  VolumeX,
  AlertTriangle,
  Volume2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Messages = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else {
        loadUsers();
        // Simulate real-time updates every 5 seconds
        const interval = setInterval(loadMessages, 5000);
        return () => clearInterval(interval);
      }
    }
  }, [user, authLoading, navigate, selectedUser]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      // Filter out current user and blocked users
      const otherUsers = response.data.filter(u => 
        u.id !== user?.id && !blockedUsers.includes(u.id)
      );
      setUsers(otherUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = (userId) => {
    setBlockedUsers(prev => [...prev, userId]);
    if (selectedUser?.id === userId) {
      setSelectedUser(null);
      setMessages([]);
    }
    toast.success('Utilisateur bloqué', {
      description: 'Vous ne recevrez plus de messages de cet utilisateur'
    });
    loadUsers();
  };

  const handleMuteUser = (userId) => {
    if (mutedUsers.includes(userId)) {
      setMutedUsers(prev => prev.filter(id => id !== userId));
      toast.success('Utilisateur réactivé', {
        description: 'Vous recevrez à nouveau des notifications'
      });
    } else {
      setMutedUsers(prev => [...prev, userId]);
      toast.success('Utilisateur mis en sourdine', {
        description: 'Vous ne recevrez plus de notifications de cet utilisateur'
      });
    }
  };

  const handleDeleteConversation = () => {
    setMessages([]);
    setShowDeleteDialog(false);
    toast.success('Conversation supprimée', {
      description: 'Tous les messages ont été effacés'
    });
  };

  const handleClearAllMessages = () => {
    setMessages([]);
    setSelectedUser(null);
    setShowClearDialog(false);
    toast.success('Messagerie vidée', {
      description: 'Toutes les conversations ont été supprimées'
    });
  };

  const handleReportUser = (userId) => {
    // In production, send report to backend
    toast.success('Utilisateur signalé', {
      description: 'Notre équipe examinera ce signalement rapidement'
    });
  };

  const loadMessages = async () => {
    if (!selectedUser) return;
    
    try {
      // Load real messages from API
      const response = await api.get(`/conversations/with/${selectedUser.id}/messages`);
      setMessages(response.data || []);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedUser) return;

    try {
      const response = await api.post(`/conversations/with/${selectedUser.id}/messages`, {
        content: newMessage.trim()
      });
      
      // Add message to local state
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      scrollToBottom();
      toast.success('Message envoyé !');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur', { description: 'Impossible d\'envoyer le message' });
    }
  };

  const handleSelectUser = (selectedUser) => {
    setSelectedUser(selectedUser);
    loadMessages();
  };

  const getRoleBadge = (role) => {
    if (role === 'owner') {
      return (
        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs">
          <Crown className="w-3 h-3 mr-1" />
          Propriétaire
        </Badge>
      );
    } else if (role === 'admin') {
      return (
        <Badge className="bg-blue-500 text-white text-xs">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      );
    } else if (role === 'moderator') {
      return (
        <Badge className="bg-purple-500 text-white text-xs">
          <Shield className="w-3 h-3 mr-1" />
          Modérateur
        </Badge>
      );
    }
    return null;
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-2xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-200">
      <Navbar />
      <div className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Messagerie</h1>
                <p className="text-sm text-gray-600">Communiquez avec la communauté</p>
              </div>
            </div>
            <Button
              onClick={() => setShowClearDialog(true)}
              variant="destructive"
              size="sm"
              className="gap-2"
            >
              <Trash2 className="w-3 h-3" />
              Vider
            </Button>
          </div>

          <Card className="border-none shadow-lg overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 h-[550px]">
                {/* Users List - Compact */}
                <div className="border-r border-gray-200 bg-gradient-to-b from-white to-gray-50">
                  <div className="p-3 border-b border-gray-200 bg-white">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Rechercher..."
                        className="pl-8 h-9 text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <ScrollArea className="h-[640px]">
                    {filteredUsers.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>Aucun utilisateur trouvé</p>
                      </div>
                    ) : (
                      <div className="p-2">
                        {filteredUsers.map((u) => (
                          <div
                            key={u.id}
                            onClick={() => handleSelectUser(u)}
                            className={`p-3 rounded-lg cursor-pointer transition-all duration-200 mb-2 ${
                              selectedUser?.id === u.id
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                                : 'bg-white hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <Avatar className="w-12 h-12">
                                  <AvatarImage src={u.avatar} />
                                  <AvatarFallback>{u.name.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-emerald-500 text-emerald-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className={`font-semibold truncate ${selectedUser?.id === u.id ? 'text-white' : 'text-slate-800'}`}>
                                    {u.name}
                                  </p>
                                  {getRoleBadge(u.role)}
                                </div>
                                <p className={`text-xs truncate ${selectedUser?.id === u.id ? 'text-blue-100' : 'text-gray-500'}`}>
                                  {u.email}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Messages Area */}
                <div className="lg:col-span-2 flex flex-col">
                  {selectedUser ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b border-gray-200 bg-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={selectedUser.avatar} />
                              <AvatarFallback>{selectedUser.name.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-800">{selectedUser.name}</p>
                                {getRoleBadge(selectedUser.role)}
                                {mutedUsers.includes(selectedUser.id) && (
                                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                                    <VolumeX className="w-3 h-3 mr-1" />
                                    En sourdine
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                                En ligne
                              </p>
                            </div>
                          </div>

                          {/* Actions Menu */}
                          <div className="flex items-center gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="icon"
                                  className="hover:bg-gray-100"
                                >
                                  <MoreVertical className="w-5 h-5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem
                                  onClick={() => handleMuteUser(selectedUser.id)}
                                  className="cursor-pointer"
                                >
                                  {mutedUsers.includes(selectedUser.id) ? (
                                    <>
                                      <Volume2 className="w-4 h-4 mr-2" />
                                      Réactiver les notifications
                                    </>
                                  ) : (
                                    <>
                                      <VolumeX className="w-4 h-4 mr-2" />
                                      Mettre en sourdine
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setShowDeleteDialog(true)}
                                  className="cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Supprimer la conversation
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleReportUser(selectedUser.id)}
                                  className="cursor-pointer text-orange-600"
                                >
                                  <AlertTriangle className="w-4 h-4 mr-2" />
                                  Signaler l'utilisateur
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleBlockUser(selectedUser.id)}
                                  className="cursor-pointer text-red-600"
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Bloquer l'utilisateur
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>

                      {/* Messages */}
                      <ScrollArea className="flex-1 p-4 bg-gray-50">
                        <div className="space-y-4">
                          {messages.map((msg) => {
                            const isOwn = msg.sender_id === user.id;
                            return (
                              <div
                                key={msg.id}
                                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                                    isOwn
                                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                                      : 'bg-white text-slate-800 shadow-md'
                                  }`}
                                >
                                  <p className="text-sm">{msg.content}</p>
                                  <p
                                    className={`text-xs mt-1 ${
                                      isOwn ? 'text-blue-100' : 'text-gray-500'
                                    }`}
                                  >
                                    {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>

                      {/* Message Input */}
                      <div className="p-4 border-t border-gray-200 bg-white">
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                          <Input
                            placeholder="Écrivez votre message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            type="submit"
                            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                            disabled={!newMessage.trim()}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </form>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-semibold mb-2">Aucune conversation sélectionnée</p>
                        <p className="text-sm">Choisissez un utilisateur pour commencer à discuter</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delete Conversation Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer la conversation ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action supprimera tous les messages de cette conversation. 
                  Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConversation}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Clear All Messages Dialog */}
          <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Vider toute la messagerie ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action supprimera TOUTES vos conversations et messages. 
                  Cette action est irréversible et ne peut pas être annulée.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearAllMessages}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Tout supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Messages;
